/**
 * Dialogs Module
 * Utilities for working with native <dialog> elements
 */

const DIALOG_VIEWPORT_HEIGHT_VAR = '--dialog-viewport-height';

let viewportSyncBound = false;
let viewportSyncFrame = null;
let scrollLockDepth = 0;
let lockedScrollY = 0;
let lockUsedFixedBody = false;
let previousBodyStyles = null;

export function initDialogs() {
    bindViewportSync();

    // Set up close buttons for all dialogs
    document.querySelectorAll('dialog .dialog-close, dialog .gallery-close').forEach(button => {
        button.addEventListener('click', () => {
            const dialog = button.closest('dialog');
            closeDialog(dialog);
        });
    });

    // Close dialog when clicking backdrop (desktop only)
    document.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', (e) => {
            // Only close if clicking the dialog itself (backdrop), not its content
            if (e.target === dialog) {
                closeDialog(dialog);
            }
        });
    });

    // ESC key handling is built into <dialog> but we can add custom behavior
    document.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
            onDialogClose(dialog);
        });
    });
}

/**
 * Open a dialog by ID or element
 */
export function openDialog(dialogOrId, options = {}) {
    const dialog = typeof dialogOrId === 'string'
        ? document.getElementById(dialogOrId)
        : dialogOrId;

    if (!dialog) {
        return;
    }

    // Restore any iframe src that was cleared on close
    dialog.querySelectorAll('iframe[data-original-src]').forEach(iframe => {
        iframe.src = iframe.dataset.originalSrc;
        delete iframe.dataset.originalSrc;
    });

    syncViewportHeightSoon();
    dialog.showModal();
    syncViewportHeightSoon();

    // Lock background scroll while dialog is open
    lockBackgroundScroll();

    if (options.autoFocus !== false) {
        const firstFocusable = dialog.querySelector('button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            setTimeout(() => firstFocusable.focus(), 100);
        }
    }

    if (options.onOpen) {
        options.onOpen(dialog);
    }
}

/**
 * Close a dialog
 */
export function closeDialog(dialogOrId) {
    const dialog = typeof dialogOrId === 'string'
        ? document.getElementById(dialogOrId)
        : dialogOrId;

    if (!dialog) {
        return;
    }

    dialog.close();
}

/**
 * Cleanup when dialog closes
 */
function onDialogClose(dialog) {
    // Restore background scroll — only if no other dialogs are open
    const anyOpen = document.querySelector('dialog[open]');
    if (!anyOpen) {
        unlockBackgroundScroll();
    }

    // Clear any iframe content to stop videos/maps
    const iframes = dialog.querySelectorAll('iframe');
    iframes.forEach(iframe => {
        const src = iframe.src;
        iframe.src = '';
        // Store original src for potential re-opening
        iframe.dataset.originalSrc = src;
    });
}

/**
 * Load iframe content into dialog
 */
export function loadIframeInDialog(dialogId, iframeSrc, title = '') {
    const dialog = document.getElementById(dialogId);
    if (!dialog) {
        return;
    }

    // Set title if provided
    const dialogTitle = dialog.querySelector('.dialog-header h3');
    if (dialogTitle && title) {
        dialogTitle.textContent = title;
    }

    // Create or update iframe
    let iframe = dialog.querySelector('iframe');
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.setAttribute('loading', 'lazy');
        const dialogContent = dialog.querySelector('.dialog-content');
        dialogContent.appendChild(iframe);
    }

    iframe.src = iframeSrc;

    // Open dialog
    openDialog(dialog);
}

function bindViewportSync() {
    if (viewportSyncBound) {
        return;
    }

    viewportSyncBound = true;
    syncViewportHeightSoon();

    window.addEventListener('resize', syncViewportHeightSoon, { passive: true });
    window.addEventListener('orientationchange', syncViewportHeightSoon, { passive: true });

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', syncViewportHeightSoon, { passive: true });
        window.visualViewport.addEventListener('scroll', syncViewportHeightSoon, { passive: true });
    }
}

function syncViewportHeightSoon() {
    if (viewportSyncFrame !== null) {
        return;
    }

    viewportSyncFrame = window.requestAnimationFrame(() => {
        viewportSyncFrame = null;

        // On iOS, skip viewport sync when keyboard is open inside a form dialog.
        // iOS handles 100dvh natively - our sync would shrink the dialog incorrectly.
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
            const formDialog = document.getElementById('form-dialog');
            if (formDialog && formDialog.open && formDialog.classList.contains('keyboard-open')) {
                return;
            }
        }

        const viewportHeight = Math.round(
            window.visualViewport?.height
            || window.innerHeight
            || document.documentElement.clientHeight
        );

        if (!viewportHeight) {
            return;
        }

        document.documentElement.style.setProperty(DIALOG_VIEWPORT_HEIGHT_VAR, `${viewportHeight}px`);
    });
}

function lockBackgroundScroll() {
    scrollLockDepth += 1;
    if (scrollLockDepth > 1) {
        return;
    }

    previousBodyStyles = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        width: document.body.style.width,
    };

    document.body.style.overflow = 'hidden';

    lockUsedFixedBody = shouldUseFixedBodyLock();
    if (!lockUsedFixedBody) {
        return;
    }

    lockedScrollY = Math.round(window.scrollY || window.pageYOffset || 0);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
}

function unlockBackgroundScroll() {
    if (scrollLockDepth === 0) {
        return;
    }

    scrollLockDepth -= 1;
    if (scrollLockDepth > 0) {
        return;
    }

    if (previousBodyStyles) {
        document.body.style.overflow = previousBodyStyles.overflow;
        document.body.style.position = previousBodyStyles.position;
        document.body.style.top = previousBodyStyles.top;
        document.body.style.left = previousBodyStyles.left;
        document.body.style.right = previousBodyStyles.right;
        document.body.style.width = previousBodyStyles.width;
    } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
    }

    if (lockUsedFixedBody) {
        window.scrollTo(0, lockedScrollY);
    }

    previousBodyStyles = null;
    lockUsedFixedBody = false;
    lockedScrollY = 0;
}

function shouldUseFixedBodyLock() {
    return window.matchMedia('(max-width: 700px)').matches;
}
